'use client';

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Search, X, Bus, MapPin, Hash, CreditCard, ChevronRight, Activity, Loader2 } from 'lucide-react';
import { useDebounce } from '@/lib/hooks/useDebounce';
import { crewService, CrewMember } from '@/lib/supabase/services/crewService';
import { dutyService, DutyAssignment } from '@/lib/supabase/services/dutyService';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SearchResult {
  id: string;
  sourceType: 'crew' | 'duty';
  type: string;
  name: string;
  employeeId: string;
  route: string;
  license: string;
  status: string;
  extra?: string; // bus number or shift
  matchedField: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function highlight(text: string, query: string): React.ReactNode {
  if (!query || !text) return text ?? '';
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-primary/20 text-primary rounded-sm px-0.5 not-italic font-600">
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
}

const statusColors: Record<string, string> = {
  'On Trip':   'bg-positive/15 text-positive',
  'Available': 'bg-primary/15 text-primary',
  'Rest':      'bg-accent/15 text-accent',
  'Leave':     'bg-warning/15 text-warning',
  'Suspended': 'bg-destructive/15 text-destructive',
  'Active':    'bg-positive/15 text-positive',
  'Scheduled': 'bg-primary/15 text-primary',
  'Completed': 'bg-muted text-muted-foreground',
  'Cancelled': 'bg-destructive/15 text-destructive',
  'Delayed':   'bg-warning/15 text-warning',
};

function crewToResult(c: CrewMember, q: string): SearchResult | null {
  const fields: { key: string; label: string; value: string }[] = [
    { key: 'name',           label: 'Name',     value: c.name },
    { key: 'crewCode',       label: 'ID',        value: c.crewCode },
    { key: 'badgeOrLicense', label: 'License',   value: c.badgeOrLicense },
    { key: 'route',          label: 'Route',     value: c.route },
    { key: 'status',         label: 'Status',    value: c.status },
    { key: 'phone',          label: 'Phone',     value: c.phone },
    { key: 'depot',          label: 'Depot',     value: c.depot },
    { key: 'grade',          label: 'Grade',     value: c.grade },
  ];
  const matched = fields.find((f) => f.value?.toLowerCase().includes(q.toLowerCase()));
  if (!matched) return null;
  return {
    id: c.id,
    sourceType: 'crew',
    type: c.role,
    name: c.name,
    employeeId: c.crewCode,
    route: c.route || '—',
    license: c.badgeOrLicense,
    status: c.status,
    extra: c.depot,
    matchedField: matched.label,
  };
}

function dutyToResult(d: DutyAssignment, q: string): SearchResult | null {
  const fields: { key: string; label: string; value: string }[] = [
    { key: 'dutyCode',      label: 'Duty ID',       value: d.dutyCode },
    { key: 'driver1Name',   label: 'Driver',         value: d.driver1Name },
    { key: 'driver2Name',   label: 'Driver 2',       value: d.driver2Name },
    { key: 'conductorName', label: 'Conductor',      value: d.conductorName },
    { key: 'route',         label: 'Route',          value: d.route },
    { key: 'routeName',     label: 'Route Name',     value: d.routeName },
    { key: 'busNumber',     label: 'Bus No.',        value: d.busNumber },
    { key: 'status',        label: 'Status',         value: d.status },
    { key: 'depot',         label: 'Depot',          value: d.depot },
  ];
  const matched = fields.find((f) => f.value?.toLowerCase().includes(q.toLowerCase()));
  if (!matched) return null;
  return {
    id: d.id,
    sourceType: 'duty',
    type: 'Duty',
    name: d.dutyCode,
    employeeId: d.route,
    route: d.routeName || d.route,
    license: d.busNumber,
    status: d.status,
    extra: `${d.shift} · ${d.dutyDate}`,
    matchedField: matched.label,
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function GlobalSearchBar() {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const [crewData, setCrewData] = useState<CrewMember[]>([]);
  const [dutyData, setDutyData] = useState<DutyAssignment[]>([]);
  const [dataLoaded, setDataLoaded] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const debouncedQuery = useDebounce(query, 200);

  // Load data once when user starts typing
  const loadData = useCallback(async () => {
    if (dataLoaded) return;
    setLoading(true);
    try {
      const [crew, duties] = await Promise.all([
        crewService.getAll(),
        dutyService.getAll(),
      ]);
      setCrewData(crew);
      setDutyData(duties);
      setDataLoaded(true);
    } catch {
      // silent fail — results will just be empty
    } finally {
      setLoading(false);
    }
  }, [dataLoaded]);

  useEffect(() => {
    if (query.trim().length > 0) {
      loadData();
    }
  }, [query, loadData]);

  const results = useMemo<SearchResult[]>(() => {
    const q = debouncedQuery.trim();
    if (!q || q.length < 1) return [];

    const crewResults = crewData
      .map((c) => crewToResult(c, q))
      .filter((r): r is SearchResult => r !== null);

    const dutyResults = dutyData
      .map((d) => dutyToResult(d, q))
      .filter((r): r is SearchResult => r !== null);

    return [...crewResults, ...dutyResults].slice(0, 10);
  }, [debouncedQuery, crewData, dutyData]);

  const showDropdown = open && focused && query.trim().length > 0;

  const handleClear = useCallback(() => {
    setQuery('');
    inputRef.current?.focus();
  }, []);

  // Keyboard shortcut: "/" to focus
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === '/' && document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA') {
        e.preventDefault();
        inputRef.current?.focus();
      }
      if (e.key === 'Escape') {
        setOpen(false);
        setFocused(false);
        inputRef.current?.blur();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setFocused(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const crewCount = results.filter((r) => r.sourceType === 'crew').length;
  const dutyCount = results.filter((r) => r.sourceType === 'duty').length;

  return (
    <div ref={containerRef} className="relative w-full max-w-2xl">
      {/* Search Input */}
      <div
        className={`flex items-center gap-2.5 px-4 py-2.5 rounded-xl border bg-card transition-all duration-200 ${
          focused
            ? 'border-primary ring-2 ring-primary/15 shadow-sm'
            : 'border-border hover:border-border/80'
        }`}
      >
        {loading ? (
          <Loader2 size={16} className="shrink-0 text-primary animate-spin" />
        ) : (
          <Search size={16} className={`shrink-0 transition-colors ${focused ? 'text-primary' : 'text-muted-foreground'}`} />
        )}
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => { setFocused(true); setOpen(true); }}
          placeholder="Search crew name, ID, license, route, status…"
          className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground/60 outline-none min-w-0"
        />
        {query && (
          <button
            onClick={handleClear}
            className="shrink-0 p-0.5 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            aria-label="Clear search"
          >
            <X size={13} />
          </button>
        )}
        {!focused && (
          <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-border text-[10px] font-mono text-muted-foreground/60 shrink-0">
            /
          </kbd>
        )}
      </div>

      {/* Dropdown Results */}
      {showDropdown && (
        <div className="absolute top-full left-0 right-0 mt-1.5 bg-card border border-border rounded-xl shadow-lg z-50 overflow-hidden">
          {loading && results.length === 0 ? (
            <div className="flex items-center justify-center gap-2 py-8 text-muted-foreground">
              <Loader2 size={16} className="animate-spin" />
              <span className="text-sm">Searching…</span>
            </div>
          ) : results.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 gap-2 text-muted-foreground">
              <Search size={20} className="opacity-40" />
              <p className="text-sm">No results for <span className="font-500 text-foreground">"{query}"</span></p>
              <p className="text-xs opacity-60">Try a name, crew ID, license, route, or status</p>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="px-3 pt-2.5 pb-1.5 flex items-center justify-between border-b border-border/50">
                <span className="text-[11px] font-600 text-muted-foreground uppercase tracking-wider">
                  {results.length} result{results.length !== 1 ? 's' : ''}
                </span>
                <div className="flex items-center gap-2">
                  {crewCount > 0 && (
                    <span className="text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded font-500">
                      {crewCount} Crew
                    </span>
                  )}
                  {dutyCount > 0 && (
                    <span className="text-[10px] bg-accent/10 text-accent px-1.5 py-0.5 rounded font-500">
                      {dutyCount} Duty
                    </span>
                  )}
                </div>
              </div>

              {/* Results list */}
              <ul className="py-1 max-h-80 overflow-y-auto divide-y divide-border/30">
                {results.map((result) => (
                  <li
                    key={`${result.sourceType}-${result.id}`}
                    className="flex items-center gap-3 px-3 py-2.5 hover:bg-muted/60 cursor-pointer transition-colors group"
                  >
                    {/* Avatar / Icon */}
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-xs font-700 ${
                      result.sourceType === 'duty' ?'bg-accent/15 text-accent'
                        : result.type === 'Driver' ?'bg-primary/15 text-primary' :'bg-positive/15 text-positive'
                    }`}>
                      {result.sourceType === 'duty'
                        ? <Bus size={14} />
                        : result.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                    </div>

                    {/* Main info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-500 text-foreground truncate">
                          {highlight(result.name, debouncedQuery)}
                        </span>
                        <span className={`text-[10px] font-600 px-1.5 py-0.5 rounded-full ${
                          result.type === 'Driver' ?'bg-primary/10 text-primary'
                            : result.type === 'Conductor' ?'bg-positive/10 text-positive' :'bg-accent/10 text-accent'
                        }`}>
                          {result.type}
                        </span>
                        <span className={`text-[10px] font-600 px-1.5 py-0.5 rounded ${statusColors[result.status] ?? 'bg-muted text-muted-foreground'}`}>
                          {highlight(result.status, debouncedQuery)}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                        <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                          <Hash size={9} />
                          {highlight(result.employeeId, debouncedQuery)}
                        </span>
                        {result.sourceType === 'crew' && (
                          <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                            <CreditCard size={9} />
                            {highlight(result.license, debouncedQuery)}
                          </span>
                        )}
                        <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                          <MapPin size={9} />
                          {highlight(result.route, debouncedQuery)}
                        </span>
                        {result.extra && (
                          <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                            <Activity size={9} />
                            {result.extra}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Matched field badge */}
                    <div className="shrink-0 text-[10px] text-muted-foreground bg-muted/60 px-2 py-1 rounded-md">
                      {result.matchedField}
                    </div>

                    <ChevronRight size={13} className="shrink-0 text-muted-foreground/40 group-hover:text-muted-foreground transition-colors" />
                  </li>
                ))}
              </ul>

              <div className="px-3 py-2 border-t border-border/50 text-[11px] text-muted-foreground/60 text-center">
                Searching across Crew Name · ID · License · Route · Status · Duty Assignments
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
